// src/settingComponent/apiSetting/ApiPopupEdit.jsx
import React, { useState, useEffect } from 'react';
import './ApiPopupEdit.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const ApiPopupEdit = ({ products = [], keyData, onClose, onSave }) => {
  const [keyName, setKeyName] = useState(keyData.apiKeyName || '');
  const [permissionMode, setPermissionMode] = useState('all');
  const [selectedResources, setSelectedResources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const assigned = keyData.apiKeyProductsList || [];
    if (assigned.length === products.length) {
      setPermissionMode('all');
      setSelectedResources([]);
    } else {
      setPermissionMode('restricted');
      setSelectedResources(assigned);
    }
  }, [keyData, products]);

  const functions = getFunctions();
  const modifyKeyAPICallable = httpsCallable(functions, 'modifyKeyAPI');

  const handleSave = async () => {
    setIsLoading(true);
    const productsList =
      permissionMode === 'all'
        ? products.map(p => p.productName)
        : selectedResources;

    const payload = {
      keyName,
      productsList,
      apiKey: keyData.apiKey,
      isDelete: false,
    };
    // console.log('modifyKeyAPI payload:', payload);

    try {
      await modifyKeyAPICallable(payload);
      onSave();
    } catch (err) {
      // console.error('Error calling modifyKeyAPI:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const turnOn = name =>
    setSelectedResources(prev =>
      prev.includes(name) ? prev : [...prev, name]
    );
  const turnOff = name =>
    setSelectedResources(prev =>
      prev.filter(x => x !== name)
    );

  const saveDisabled =
    isLoading ||
    (permissionMode === 'restricted' && selectedResources.length === 0);

  return (
    <div className="api-popup-overlay">
      <div className="api-popup-modal">
        <div className="api-popup-header">
          <h2>Edit secret key</h2>
          <button className="api-popup-close" onClick={onClose}>&times;</button>
        </div>

        <div className="api-popup-body">
          {/* Name field */}
          <div className="api-popup-row">
            <label className="api-popup-title">
              Name <span className="optional">(optional)</span>
            </label>
            <input
              type="text"
              className="api-popup-input"
              value={keyName}
              onChange={e => setKeyName(e.target.value)}
              placeholder="My Secret Key"
              maxLength={10}
            />
          </div>

          {/* Permissions toggle */}
          <div className="api-popup-row">
            <label className="api-popup-title">Permissions</label>
            <div className="api-popup-permissions">
              <button
                className={`api-perm-button ${permissionMode === 'all' ? 'active' : ''}`}
                onClick={() => setPermissionMode('all')}
              >
                All
              </button>
              <button
                className={`api-perm-button ${permissionMode === 'restricted' ? 'active' : ''}`}
                onClick={() => setPermissionMode('restricted')}
              >
                Restricted
              </button>
            </div>
          </div>

          {/* Resources list when restricted */}
          {permissionMode === 'restricted' && (
            <>
              <div className="api-popup-resources">
                <div className="resources-header">
                  <span className="resources-title">Resources</span>
                  <span className="resources-permissions-label">Permissions</span>
                </div>
                {products.map(p => (
                  <div className="resource-row" key={p.productName}>
                    <div className="resource-info">
                      <div
                        className="resource-name"
                        data-description={p.productDescription}
                      >
                        {p.productName}
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="resource-info-icon"
                        />
                      </div>
                      {Array.isArray(p.productEndpoints) && (
                        <div className="resource-endpoints">
                          {p.productEndpoints.map((ep, i) => (
                            <div key={i}>{ep}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="resource-permissions">
                      <button
                        className={`api-perm-button ${
                          !selectedResources.includes(p.productName) ? 'active' : ''
                        }`}
                        onClick={() => turnOff(p.productName)}
                      >
                        Off
                      </button>
                      <button
                        className={`api-perm-button ${
                          selectedResources.includes(p.productName) ? 'active' : ''
                        }`}
                        onClick={() => turnOn(p.productName)}
                      >
                        On
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Disclaimer */}
              <div className="api-popup-disclaimer">
                <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
                Permission changes may take a few minutes to take effect.
              </div>
            </>
          )}
        </div>

        {/* Footer with Cancel / Save */}
        <div className="api-popup-footer">
          <button className="api-cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="api-confirm-button"
            onClick={handleSave}
            disabled={saveDisabled}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiPopupEdit;
